import { defineStore } from "pinia";
import { USER_TYPE, HELP_KIND_TYPE, FORM_INPUT_MODE, ACCOUNT_FORM_TARGET } from '/constants/common.js'

export const useAccountStore = defineStore("account", () => {
  const { locale } = useI18n()
  const data = ref(null)
  const emailFormatOptions = ref([]);
  const occupationOptions = ref([]);
  const employeeNumberOptions = ref([]);
  const businessCategories = ref([]);
  const helpNavMenus = ref([]);
  const reasonOfCancelOptions = ref([]);
  const planOptions = ref([]);
  const userStatusOptions = ref([]);
  const faqHelpSideMenus = ref([]);
  const faqHelpMenuContents = ref([]);
  const guideHelpSideMenus = ref([]);
  const guideHelpMenuContents = ref([]);
  const trainingHelpSideMenus = ref([]);
  const trainingHelpMenuContents = ref([]);
  const masterData = ref({})
  const userInformation = ref({})
  const targetPage = ref(ACCOUNT_FORM_TARGET.UPDATE)
  const accountInfo = ref({});
  const staffList = ref(null);
  const staffListCount = ref(0);
  const sendingEmailList = ref([]);
  const activeStaffList = ref([]);
  const activeStaffListCount = ref(0);
  
  const inputMode = ref(FORM_INPUT_MODE.EDIT);

  const resetStoreCache = () => {
    faqHelpSideMenus.value = []
    faqHelpMenuContents.value = []
    guideHelpSideMenus.value = []
    guideHelpMenuContents.value = []
    trainingHelpSideMenus.value = []
    trainingHelpMenuContents.value = []
  };

  const getCommonFaq = async () => {
    const params = {
      menuId: 3199,
      locale: locale.value
    }
    const res = await usePost('/help/content/q/m', params)
    data.value = res.data.data.data
    return res.data.data.data
  }

  const getHelpNavMenusFromServer = async () => {
    if (helpNavMenus.value.length) {
      return
    }
    const data = await usePost('/code/q/a', { mcdId: 31, locale: locale.value })
    const rows = data.data.data
    helpNavMenus.value = rows.map(({ codeId, codeName, ...rest }) => {
      return {
        text: codeName,
        value: codeId,
        codeName,
        codeId,
        ...rest
      }
    })
  }
  
  const getBusinessCategories = (optionalOption) => {
    return [optionalOption, ...businessCategories.value]
  }

  const getHelpNavMenus = async (userType) => {
    const componentStore = useComponentStore()
    if (!userType) {
      return []
    }
    if (helpNavMenus.value.length > 0) {
      const helpType = Number(componentStore.helpType)
      if ((USER_TYPE.DEMO === Number(userType) && USER_TYPE.GUEST === helpType) || USER_TYPE.GUEST === Number(userType)) {
        return helpNavMenus.value.reduce((previous, current) => {
          if (HELP_KIND_TYPE.TRAINING !== current.codeId) {
            previous.push(current)
          }
          return previous
        }, [])
      } else if ((USER_TYPE.DEMO === Number(userType) && USER_TYPE.SUPPLIER === helpType || [USER_TYPE.SUPPLIER].includes(Number(userType)))) {
        return helpNavMenus.value
      } else {
        console.error('unhandled option')
      }
    } else {
      return []
    }
  }

  const getHelpSideMenusFromServer = async ({ userType, helpKind, refresh }) => {
    const componentStore = useComponentStore()
    const mapper = {
      [HELP_KIND_TYPE.FAQ]: {
        menu: faqHelpSideMenus,
        content: faqHelpMenuContents
      },
      [HELP_KIND_TYPE.GUIDE]: {
        menu: guideHelpSideMenus,
        content: guideHelpMenuContents
      },
      [HELP_KIND_TYPE.TRAINING]: {
        menu: trainingHelpSideMenus,
        content: trainingHelpMenuContents
      }
    }
  
    const target = mapper[helpKind]

    if (!refresh && target.menu.value.length) {
      return {}
    }
    const data = await usePost('/help/q/t', {
      userType: userType !== USER_TYPE.DEMO ? userType : componentStore.helpType,
      helpKind,
      locale: locale.value
    })
    const rows = data.data.data
    const contentOfMenu = await Promise.all(rows.map(async ({ menuId }) => {
        const res = await usePost('/help/content/q/m', { menuId, locale: locale.value })
        return res.data.data
      })
    )
    const content = await Promise.all(
      contentOfMenu.map(async (item) => {
        return item.data
      })
    )
    target.menu.value = rows
    target.content.value = content

    return { helpKind, menu: rows, content }
  }

  const selectedUserStatusOptions = (selections) => {
    return selections
      .map((id) => userStatusOptions.value.find(({ value }) => value === id))
      .filter((item) => item !== undefined)
  }

  const getUserStatusFromServer = async () => {
    return apiWrapper(async () => {
      if (!userStatusOptions.value.length) {
        const data = await callPostWithToken(
          `/code/q/a/`,
          {
            mcdId: 17,
            locale: locale.value
          },
        )

        const rows = data.data.data

        const result = rows.map((data) => ({
          text: data.codeName,
          value: data.codeId
        }))

        userStatusOptions.value = result
      }
    })
  }
  
  const actionGetBusinessCategories = async () => {
    const config = useRuntimeConfig()
    if (!(businessCategories.value && businessCategories.value.length)) {
      const params = new URLSearchParams({ language: locale.value})
      const { data } = await $fetch(`${config.public.sso.apiURL}/industries?` + params, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        }
      })
      const result = data.map(item => {
        return {
          text: item.name,
          value: item.code,
          codeId: item.code
        }
      })
      businessCategories.value = result
      return result
    }
    return businessCategories.value
  }
  
  const getMasterData = async (body) => {
    const params = new URLSearchParams(body)
    const config = useRuntimeConfig()
    const { data } = await $fetch(`${config.public.sso.apiURL}/master-data?` + params, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      }
    })
    masterData.value = data
  }
  
  const getPlansFromServer = () => {
    return apiWrapper(async () => {
      if (!(planOptions.value && planOptions.value.length)) {
        const data = await usePost(
          `/code/q/a/`,
          {
            mcdId: 30,
            locale: locale.value
          }
        )
        
        const rows = data.data.data
        const result = rows.map(data => {
          return {
            text: data.codeName,
            value: data.codeId
          }
        })
        
        planOptions.value = result
      }
    })
  }
  
  const getUserInformation = async (email) => {
    const config = useRuntimeConfig()
    const headers = {
      'Content-Type': 'application/json',
      'X-API-KEY': config.public.sso.secretKey,
    }
    const params = new URLSearchParams({ email })
    const result = await $fetch(`${config.public.sso.apiURL}/m2m/users/by-email?` + params, {
      method: "GET",
      headers
    })
    userInformation.value = result.data
    return result.data
  }

  const editUser = async (params) => {
    return apiWrapper(async () => {
      try {
        const { data } = await usePost('/user/u', {
          ...params,
          ...provideDateOfBirth({
            year: params.year,
            month: params.month,
            day: params.day
          })
        })
        return data
      } catch (e) {
        console.error(e)
        throw e
      }
    })
  }

  const getEmailFormatOptionsFromServer = async () => {
    return apiWrapper(async () => {
      if (!emailFormatOptions.value.length) {
        const data = await callPostWithToken(
          `/code/q/a/`,
          {
            mcdId: 14,
            locale: locale.value
          },
        )

        const rows = data.data.data

        const result = rows.map((data) => ({
          text: data.codeName,
          value: data.codeId
        }))

        emailFormatOptions.value = result
      }
    })
  }
  
  const changelogFlagOnStaffList = async ({ index, value }) => {
    staffList.value[index].logFlag = value
  }
  
  const getStaffList = async ({ overwrite, ...rest }) => {
    if (!overwrite && staffList.value) {
      return
    }
    if (!overwrite) {
      staffList.value = []
    }
    const result = await usePost('/user/group/q/s/a',
      {
        locale: locale.value,
        ...rest
      }
    )
    staffList.value = result.data.data.data
    staffListCount.value = result.data.data.total
    return { data: result.data.data.data, totalCount: result.data.data.total }
  }
  
  const changeUserStatus = async ( { userId, userStatus }) => {
    const result = await usePost(
      `/user/u/s`,
      {
        userId,
        userStatus
      }
    )
    return result.data.data
  }
  
  const existUserByEmail = async ({ email, userPosition }) => {
    const result = await usePost(`/join/q/e`, {
      email,
      userPosition,
      isAdmin: "N",
    });
    return { isExist: result.data.data.isExist, errorCode: result.data.data.errorCode };
  };
  
  const getActiveStaffList = async ({ overwrite, ...rest }) => {
    if (!overwrite && activeStaffList.value) {
      return
    }
    if (!overwrite) {
      activeStaffList.value = []
      activeStaffListCount.value = 0
    }
    const result = await usePost('/user/group/q/s',
      {
        locale: locale.value,
        ...rest
      }
    )
    
    activeStaffList.value = result.data.data.data
    activeStaffListCount.value = result.data.data.total
    return { data: result.data.data.data, totalCount: result.data.data.total }
  }
  
  const inviteMember = ({ email, userType }) => {
    return apiWrapper(async () => {
      const mapper = {
        [USER_TYPE.SUPPLIER]: 'ys',
        [USER_TYPE.USER]: 'gs'
      }
      const result = await usePost(
        `/join/${mapper[userType]}/r`,
        {
          email
        }
      )
      return result.data
    })
  }
  
  const changeUserLogAccessStatus = async (params) => {
    const result = await usePost(`/user/u/l`,
      params
    )
    return result.data.data
  }

  return {
    data,
    emailFormatOptions,
    occupationOptions,
    employeeNumberOptions,
    businessCategories,
    helpNavMenus,
    reasonOfCancelOptions,
    planOptions,
    userStatusOptions,
    faqHelpSideMenus,
    faqHelpMenuContents,
    guideHelpSideMenus,
    guideHelpMenuContents,
    trainingHelpSideMenus,
    trainingHelpMenuContents,
    masterData,
    resetStoreCache,
    getHelpNavMenus,
    getHelpNavMenusFromServer,
    getHelpSideMenusFromServer,
    getBusinessCategories,
    getCommonFaq,
    selectedUserStatusOptions,
    getUserStatusFromServer,
    actionGetBusinessCategories,
    getMasterData,
    getPlansFromServer,
    getUserInformation,
    userInformation,
    editUser,
    inputMode,
    getEmailFormatOptionsFromServer,
    targetPage,
    changelogFlagOnStaffList,
    staffListCount,
    staffList,
    getStaffList,
    changeUserStatus,
    existUserByEmail,
    sendingEmailList,
    activeStaffList,
    activeStaffListCount,
    getActiveStaffList,
    inviteMember,
    changeUserLogAccessStatus
  };
});
